/**
 * questionBank.js
 * Misconception-tagged question bank for the Predict→Observe→Explain loop.
 *
 * Every WRONG option carries a `tag` identifying a known physics misconception.
 * The store logs tags; 2+ hits on the same tag = confirmed misconception,
 * which is reported on the Finish Screen (and later drives adaptive levels).
 */

// ─── Misconception taxonomy ───────────────────────────────────────
export const MISCONCEPTIONS = {
  CENTRIFUGAL_FORCE: {
    label: "Centrifugal force",
    description:
      "Believing an outward 'centrifugal force' acts on the cart. In an inertial frame there is no outward force — the cart needs a NET INWARD (centripetal) force to follow a curve.",
    remedy:
      "Draw the FBD: only N, mg and friction act. Their inward resultant must equal mv²/r.",
  },
  SPEED_EQUALS_STABILITY: {
    label: "Faster = more stable",
    description:
      "Believing higher speed presses the cart onto the track and makes curves safer. Required centripetal force grows as v², so higher speed makes curves HARDER to hold.",
    remedy:
      "Compare F_needed = mv²/r at two speeds — doubling v quadruples the needed force.",
  },
  FORCE_IN_MOTION_DIRECTION: {
    label: "Motion needs a forward force",
    description:
      "Believing a force must act in the direction of motion to keep an object moving (the 'impetus' idea). Moving objects keep moving without any forward force — forces change motion.",
    remedy:
      "Newton's 1st law: in flight, the ONLY force is gravity (down), yet the cart keeps moving forward.",
  },
  NORMAL_EQUALS_WEIGHT: {
    label: "Normal force always equals mg",
    description:
      "Believing N = mg always. The normal force adjusts to whatever the situation requires — on a hilltop it is LESS than mg, and reaches zero at lift-off.",
    remedy:
      "At a crest: mg − N = mv²/r, so N = m(g − v²/r). N shrinks as v grows.",
  },
  MASS_DEPENDENCE: {
    label: "Heavier changes the safe speed",
    description:
      "Believing mass changes the maximum safe speed or how fast things fall. Mass cancels out: v_max = √(g·r·cosθ) has no m in it, and all masses fall with the same g.",
    remedy:
      "Write the equation with m on both sides — divide it out and m disappears.",
  },
  GRAPH_AS_PICTURE: {
    label: "Graph read as a picture",
    description:
      "Reading a v-t graph as if it were a drawing of the track. The graph shows SPEED versus time, not the shape of the hill.",
    remedy:
      "Going downhill the track drops but speed RISES — the v-t line goes UP.",
  },
  FORMULA_ENERGY: {
    label: "Mixing energy equations with circular motion",
    description:
      "Reaching for v = √(2gh) (energy / free fall) when the question is about staying on a curve. The danger in a curve is centripetal — it depends on the RADIUS, not on height dropped.",
    remedy:
      "Ask first: what limits the motion here? If it's turning, the equation must contain r.",
  },
  FORMULA_KINEMATICS: {
    label: "Linear kinematics misapplied",
    description:
      "Using v = u + a·t for a safe-speed limit. Kinematic equations describe how speed CHANGES over time; the curve limit is a condition at one instant and contains no time at all.",
    remedy:
      "A limit equation comes from a force balance (N ≥ 0, F ≤ μN), not from time evolution.",
  },
  UNITS_DIMENSIONS: {
    label: "Units / dimensional slips",
    description:
      "Writing v_max = g·r. Check dimensions: g·r has units m²/s² — that is v², not v. Dimensional analysis catches this instantly.",
    remedy:
      "Always check: [g·r] = (m/s²)(m) = m²/s² → take the square root to get m/s.",
  },
};

// ─── Helpers ──────────────────────────────────────────────────────
export function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─── STAGE 1: PREDICT — reasoning questions ──────────────────────
// Student must pick the correct physical reasoning BEFORE the
// equation is revealed and the numeric entry unlocks.

const REASONING = {
  curve: {
    question:
      "Before you calculate: WHY is there a maximum safe speed on this curve?",
    options: [
      {
        text: "To follow the curve, the track forces (N, friction, gravity components) must supply a NET INWARD force equal to mv²/r. Too fast → they can't → the cart slides off.",
        correct: true,
        whyWrong: null,
      },
      {
        text: "An outward centrifugal force pushes the cart off the curve; strong enough friction can balance it at any speed.",
        correct: false,
        tag: "CENTRIFUGAL_FORCE",
        whyWrong:
          "There is no outward force on the cart. The cart slides off because the inward force needed (mv²/r) exceeds what the track can supply.",
      },
      {
        text: "Going faster presses the cart harder into the track, so higher speed actually grips the curve better.",
        correct: false,
        tag: "SPEED_EQUALS_STABILITY",
        whyWrong:
          "The needed centripetal force grows as v² — doubling speed makes the curve 4× harder to hold, not easier.",
      },
      {
        text: "The forward driving force keeps the cart on the track; the limit exists because the engine can't push hard enough.",
        correct: false,
        tag: "FORCE_IN_MOTION_DIRECTION",
        whyWrong:
          "Forward force changes the cart's speed, not its direction. Turning requires an INWARD force — that's what runs out.",
      },
    ],
  },
  hilltop: {
    question:
      "Before you calculate: WHY can the cart fly off at the crest of the hill?",
    options: [
      {
        text: "At the crest, gravity (minus N) supplies the centripetal force: mg·cosθ − N = mv²/r. If v is too big, N would need to be negative — impossible — so the cart leaves the track.",
        correct: true,
        whyWrong: null,
      },
      {
        text: "The normal force always equals mg, so the cart is held to the track at any speed — only bumps can throw it off.",
        correct: false,
        tag: "NORMAL_EQUALS_WEIGHT",
        whyWrong:
          "N is NOT always mg. At a crest N = m(g·cosθ − v²/r): it shrinks as v grows and hits zero at lift-off speed.",
      },
      {
        text: "Above the safe speed, centrifugal force flings the cart upward off the hill.",
        correct: false,
        tag: "CENTRIFUGAL_FORCE",
        whyWrong:
          "No outward force exists. The cart leaves because gravity alone can't bend its path sharply enough (v²/r > g·cosθ) — it flies off along a tangent.",
      },
      {
        text: "A heavier cart can take the crest faster, because its weight presses it onto the track harder.",
        correct: false,
        tag: "MASS_DEPENDENCE",
        whyWrong:
          "Mass cancels: mg·cosθ = mv²/r → v_max = √(g·r·cosθ). The safe speed is the SAME for every mass.",
      },
    ],
  },
  jump: {
    question:
      "Before you calculate: what decides whether the cart clears the gap?",
    options: [
      {
        text: "In flight only gravity acts. Launch speed fixes the range R = v₀²·sin2θ/g — the cart clears the gap only if R ≥ gap width.",
        correct: true,
        whyWrong: null,
      },
      {
        text: "The cart carries a forward force from the ramp; if that force lasts long enough, it crosses the gap at any launch speed.",
        correct: false,
        tag: "FORCE_IN_MOTION_DIRECTION",
        whyWrong:
          "No forward force exists in flight — only gravity. Forward motion continues by inertia; the launch speed alone fixes how far it goes.",
      },
      {
        text: "A heavier cart falls faster, so mass decides whether the gap is cleared.",
        correct: false,
        tag: "MASS_DEPENDENCE",
        whyWrong:
          "All masses fall with the same g (ignoring air). Range R = v₀²·sin2θ/g contains no mass.",
      },
      {
        text: "The graph of the jump looks like the trajectory, so the cart lands where the v-t graph comes back down.",
        correct: false,
        tag: "GRAPH_AS_PICTURE",
        whyWrong:
          "A v-t graph is not a picture of the path. During flight, horizontal speed stays constant while the path curves down.",
      },
    ],
  },
};

// ─── STAGE 3: EXPLAIN — post-consequence questions ───────────────
// After watching the crash, student must explain WHY before retrying.

const EXPLAIN = {
  curve: {
    question: "You watched it happen. WHY did the cart slide off the curve?",
    options: [
      {
        text: "At that speed, the required centripetal force mv²/r was bigger than the track and friction could supply, so the cart continued in a straighter line — outward off the track.",
        correct: true,
        whyWrong: null,
      },
      {
        text: "Centrifugal force grew too strong and overpowered friction, throwing the cart outward.",
        correct: false,
        tag: "CENTRIFUGAL_FORCE",
        whyWrong:
          "Nothing pushed the cart outward. The track simply couldn't pull it INWARD hard enough, so the cart kept going straight (Newton's 1st law) and left the curve.",
      },
      {
        text: "The cart was too light — a heavier cart would have held the curve at this speed.",
        correct: false,
        tag: "MASS_DEPENDENCE",
        whyWrong:
          "Mass cancels in v_max = √(g·r·cosθ). A heavier cart needs MORE centripetal force in exactly the same proportion — the safe speed doesn't change.",
      },
    ],
  },
  hilltop: {
    question: "You watched it happen. WHY did the cart fly off the crest?",
    options: [
      {
        text: "The path needed centripetal acceleration v²/r greater than g·cosθ. Gravity couldn't bend the cart's path that sharply, N dropped to zero, and the cart flew off along a tangent.",
        correct: true,
        whyWrong: null,
      },
      {
        text: "The normal force (always mg) was suddenly overcome by the cart's upward centrifugal push.",
        correct: false,
        tag: "NORMAL_EQUALS_WEIGHT",
        whyWrong:
          "N is not fixed at mg — it shrinks as speed rises: N = m(g·cosθ − v²/r). At the lift-off speed N reaches zero; nothing 'pushed up'.",
      },
      {
        text: "Centrifugal force flung the cart upward off the hill.",
        correct: false,
        tag: "CENTRIFUGAL_FORCE",
        whyWrong:
          "No upward force acted. The cart simply moved too fast for gravity to curve its path down the hill — so it went straight while the track fell away beneath it.",
      },
    ],
  },
  jump: {
    question: "You watched it happen. WHY did the cart fall into the gap?",
    options: [
      {
        text: "The launch speed gave a horizontal range R = v₀²·sin2θ/g smaller than the gap width — gravity pulled it down before it crossed.",
        correct: true,
        whyWrong: null,
      },
      {
        text: "The cart ran out of forward force mid-air and stopped moving ahead.",
        correct: false,
        tag: "FORCE_IN_MOTION_DIRECTION",
        whyWrong:
          "Its horizontal speed never decreased (no air drag here). It fell short because it never had enough speed at launch — not because it 'lost' anything.",
      },
      {
        text: "The cart was too heavy, so it fell faster than a light one would.",
        correct: false,
        tag: "MASS_DEPENDENCE",
        whyWrong:
          "Heavy and light objects fall with the same g. The range formula has no mass in it — only launch speed and angle matter.",
      },
    ],
  },
};

// ─── STAGE 2: FORMULA — which equation applies, and why? ─────────
// The formula is never given; the student must SELECT it. Each wrong
// option is a classic formula-confusion, tagged for the study report.

const FORMULA = {
  curve: {
    question:
      "Which equation gives the maximum safe speed here — and why?",
    options: [
      {
        text: "v_max = √( g · r · cosθ )  —  from the force balance: the track can supply at most m·g·cosθ of inward force, so m·v²/r ≤ m·g·cosθ.",
        correct: true,
        whyWrong: null,
      },
      {
        text: "v = √( 2·g·h )  —  the speed gained falling through height h.",
        correct: false,
        tag: "FORMULA_ENERGY",
        whyWrong:
          "That's energy conversion in free fall. There's no radius in it — it can't tell you anything about holding a curve. The limit here comes from m·v²/r.",
      },
      {
        text: "v = u + a·t  —  speed after accelerating for time t.",
        correct: false,
        tag: "FORMULA_KINEMATICS",
        whyWrong:
          "That describes how speed changes over time. The curve limit is a condition on speed at one instant — it depends on r and g, not on time.",
      },
      {
        text: "v_max = g · r  —  gravity times radius.",
        correct: false,
        tag: "UNITS_DIMENSIONS",
        whyWrong:
          "Check the units: g·r is (m/s²)(m) = m²/s² — that's v², not v. The equation needs a square root: v_max = √(g·r·cosθ).",
      },
    ],
  },
  hilltop: {
    question:
      "Which equation gives the maximum safe speed at this crest — and why?",
    options: [
      {
        text: "v_max = √( g · r · cosθ )  —  at the crest N = m(g·cosθ − v²/r); N reaches 0 when v² = g·r·cosθ.",
        correct: true,
        whyWrong: null,
      },
      {
        text: "v = √( 2·g·h )  —  speed from falling through height h.",
        correct: false,
        tag: "FORMULA_ENERGY",
        whyWrong:
          "Free-fall energy — no radius, so it can't describe lift-off at a crest. The danger is v²/r exceeding g·cosθ.",
      },
      {
        text: "v = u + a·t  —  kinematics with time.",
        correct: false,
        tag: "FORMULA_KINEMATICS",
        whyWrong:
          "The lift-off condition has no time in it — it's an instantaneous force balance: N = m(g·cosθ − v²/r) ≥ 0.",
      },
      {
        text: "v_max = g · r  —  gravity times radius.",
        correct: false,
        tag: "UNITS_DIMENSIONS",
        whyWrong:
          "Dimensions: g·r = m²/s² = v². Take the square root.",
      },
    ],
  },
  jump: {
    question: "Which equation decides if the cart clears the gap — and why?",
    options: [
      {
        text: "R = v₀²·sin(2θ)/g  —  projectile range: launch speed and angle fix how far it lands.",
        correct: true,
        whyWrong: null,
      },
      {
        text: "v = √( 2·g·h )  —  speed from a height drop.",
        correct: false,
        tag: "FORMULA_ENERGY",
        whyWrong:
          "That gives impact speed after falling h — it doesn't relate launch speed to horizontal distance. You need the range equation.",
      },
      {
        text: "F = m·v²/r  —  centripetal force.",
        correct: false,
        tag: "FORMULA_KINEMATICS",
        whyWrong:
          "In flight there is no track and no circular constraint — only gravity. Projectile motion, not circular motion, applies.",
      },
    ],
  },
};

// ─── VARIANT POOLS — so repeat plays never see the same question ──
// (covers the edge cases of circular motion; drawn without repeats
//  until the pool is exhausted, then reshuffled)

const REASONING_CURVE_VARIANTS = [
  {
    question: "If you enter this curve too fast, which path does the cart actually take as it leaves the track?",
    options: [
      { text: "A straight line along the tangent — it keeps its current velocity (Newton's 1st law).", correct: true, whyWrong: null },
      { text: "Straight outward along the radius, flung by centrifugal force.", correct: false, tag: "CENTRIFUGAL_FORCE", whyWrong: "Nothing flings it outward. It simply stops turning and continues STRAIGHT — along the tangent." },
      { text: "It spirals outward gradually.", correct: false, tag: "CENTRIFUGAL_FORCE", whyWrong: "A spiral needs a continuing force pattern. With no inward grip, the path is a straight tangent line." },
      { text: "It slows down and stops on the curve.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Nothing removes its forward speed — losing grip changes DIRECTION control, not speed." },
    ],
  },
  {
    question: "Which of these would actually RAISE the safe speed of this curve?",
    options: [
      { text: "A larger radius (gentler curve) or stronger gravity — v_max = √(g·r·cosθ).", correct: true, whyWrong: null },
      { text: "A heavier cart pressing harder into the track.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "More weight = more grip BUT proportionally more force needed to turn. Mass cancels exactly." },
      { text: "A more powerful engine.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Engine force is tangential — it changes speed, never direction. The limit is about INWARD force." },
      { text: "Entering faster to 'press into' the curve.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "Faster needs MORE inward force (∝ v²) — speed is the problem, not the cure." },
    ],
  },
  {
    question: "The cart's speedometer reads CONSTANT through the curve. Is it accelerating?",
    options: [
      { text: "Yes — its direction changes, so there is an inward (centripetal) acceleration v²/r.", correct: true, whyWrong: null },
      { text: "No — constant speed means zero acceleration.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Acceleration is change of VELOCITY. Direction is changing, so a = v²/r inward, even at constant speed." },
      { text: "Yes — accelerating forward to fight the curve.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "There is no forward acceleration at constant speed. The acceleration is sideways — toward the centre." },
      { text: "Only if it's going downhill.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "Slope changes speed; the CURVE changes direction. Turning is acceleration regardless of slope." },
    ],
  },
  {
    question: "Where exactly in this curve is a derailment most likely?",
    options: [
      { text: "At the tightest point (smallest radius) — that's where v²/r demands the most force.", correct: true, whyWrong: null },
      { text: "At the exit, where you've been turning longest.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Time spent turning doesn't accumulate danger — the INSTANT condition v²/r vs available force decides, and it's worst at minimum r." },
      { text: "Anywhere — equally dangerous throughout.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "The radius varies along the curve. The minimum-radius point is the binding constraint — that's the number to compute with." },
      { text: "At the entry, because of the sudden change.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "There's no 'suddenness' penalty in the physics — only the local v²/r vs available grip matters." },
    ],
  },
  {
    question: "Two carts — 200 kg and 600 kg — enter this curve at the same speed. Which one slides off first?",
    options: [
      { text: "Neither sooner — the limit v_max = √(g·r·cosθ) has no mass in it.", correct: true, whyWrong: null },
      { text: "The heavy one — more force needed to turn it.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "It needs 3× the force but also HAS 3× the grip (forces scale with m). Mass cancels." },
      { text: "The light one — less grip on the track.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "Less grip but proportionally less force needed. The safe speed is identical." },
      { text: "Depends on their engines.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Engines act forward; the sideways (centripetal) limit doesn't involve them." },
    ],
  },
  {
    question: "On the Moon (g = 1.6 m/s²), the same curve would be…",
    options: [
      { text: "Slower — v_max = √(g·r·cosθ) shrinks when g shrinks.", correct: true, whyWrong: null },
      { text: "Faster — less gravity holding you back.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Gravity isn't 'holding you back' — it's the SOURCE of the inward grip here. Less g = less grip = lower v_max." },
      { text: "The same — the curve geometry hasn't changed.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "Geometry is only half the story: v_max depends on g too. √(1.6/10) ≈ 0.4 — the Moon curve is 2.5× slower." },
      { text: "Unlimited — things float on the Moon.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "The Moon has real gravity (1.6 m/s²) and circular motion works exactly the same — just with a lower limit." },
    ],
  },
  {
    question: "You're slightly too fast for this curve. When should you brake?",
    options: [
      { text: "BEFORE the curve — inside it, the grip is already busy turning you.", correct: true, whyWrong: null },
      { text: "Inside the curve — brake exactly where it's sharpest.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "Braking uses the same track grip that's turning you — demanding both at the sharpest point is how carts slide out. Brake on the straight." },
      { text: "No need — the curve itself slows the cart down.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Centripetal force is perpendicular to motion — it does NO work and doesn't slow you at all." },
      { text: "After the apex, while accelerating out.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "That's too late — the binding constraint is the tightest point, which you'd hit overspeed." },
    ],
  },
  {
    question: "Riding through the curve you FEEL pushed outward. What is that feeling?",
    options: [
      { text: "Your inertia — your body wants to go straight while the cart turns under you.", correct: true, whyWrong: null },
      { text: "The centrifugal force acting on your body.", correct: false, tag: "CENTRIFUGAL_FORCE", whyWrong: "In the ground frame no outward force exists. The cart accelerates inward; your body resists changing direction — that mismatch is the sensation." },
      { text: "The engine's vibration through the seat.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "It happens in unpowered carts too — it's inertia versus the cart's inward acceleration." },
      { text: "Air pressure building on the outside.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "Air has nothing to do with it — you'd feel it in vacuum. It's inertia." },
    ],
  },
  {
    question: "What supplies the centripetal force keeping this coaster cart on the curved track?",
    options: [
      { text: "The track itself — rail forces (and gravity's component) push the cart toward the curve's centre.", correct: true, whyWrong: null },
      { text: "The engine, pushing the cart around the bend.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "The engine pushes ALONG the track. The sideways push that turns you comes from the rails." },
      { text: "Centrifugal force balanced by the rails.", correct: false, tag: "CENTRIFUGAL_FORCE", whyWrong: "There's no outward force to balance. The rails supply a NET inward force = mv²/r — unbalanced, that's what turning IS." },
      { text: "The passengers leaning into the curve.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "Leaning shifts weight slightly but the rails do the turning: F = mv²/r regardless." },
    ],
  },
  {
    question: "Entering at double the safe speed would need how much inward force, compared to entering exactly at v_max?",
    options: [
      { text: "4× — force grows with v², and the track simply cannot supply it.", correct: true, whyWrong: null },
      { text: "2× — double speed, double force.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "F = mv²/r: speed is SQUARED. 2× speed → 4× force. Small overspeeds are big dangers." },
      { text: "The same — the curve is the same.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "Same curve, but YOUR demand mv²/r quadrupled. The track's supply didn't." },
      { text: "Half — speed helps you through quicker.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "Spending less time in the curve doesn't reduce the instantaneous force needed — v² rules." },
    ],
  },
  {
    question: "Why does the game pause YOU here, before the curve, rather than inside it?",
    options: [
      { text: "Because the decision is made by physics in advance — v_max is computable before you arrive.", correct: true, whyWrong: null },
      { text: "Inside the curve you could just steer harder.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "There's no 'steering harder' beyond the track's force limit — once v²/r exceeds supply, no skill saves you. Decide BEFORE." },
      { text: "Curves are random; you can only react.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "Nothing random: r and g are known, so v_max = √(g·r·cosθ) is known BEFORE entry. Physics = prediction." },
      { text: "It's just a game convention.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "It mirrors real engineering: limits are computed and respected in advance, not discovered by crashing." },
    ],
  },
];

const FORMULA_CURVE_VARIANTS = [
  {
    question: "Which CONDITION must hold for the cart to stay on this curve?",
    options: [
      { text: "m·v²/r ≤ m·g·cosθ — needed centripetal force within what the track can supply.", correct: true, whyWrong: null },
      { text: "½·m·v² ≥ m·g·h — kinetic energy beats potential energy.", correct: false, tag: "FORMULA_ENERGY", whyWrong: "Energy comparisons govern climbing hills, not holding curves. The curve is a FORCE condition with r in it." },
      { text: "v = d / t — keep average speed reasonable.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Average speed says nothing about the instantaneous turning demand v²/r at the tightest point." },
      { text: "F_engine ≥ μ·N — engine overcomes friction.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "That's about maintaining speed along the track — the danger here is sideways: m·v²/r vs available inward force." },
    ],
  },
  {
    question: "Which formula is the RIGHT TOOL for this curve — and which are decoys?",
    options: [
      { text: "v_max = √(g·r·cosθ) — a force-balance limit containing the radius.", correct: true, whyWrong: null },
      { text: "v = 2πr/T — speed from the period of revolution.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "That converts lap time to speed — it describes motion, it doesn't LIMIT it. No safety condition inside." },
      { text: "p = m·v — momentum.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Momentum is conserved bookkeeping for collisions — no radius, no grip, no limit." },
      { text: "v = √(2·g·h) — speed after dropping height h.", correct: false, tag: "FORMULA_ENERGY", whyWrong: "Free-fall energy. The curve's danger depends on r, which this formula doesn't contain." },
    ],
  },
  {
    question: "Which quantity does NOT appear in the safe-speed formula v_max = √(g·r·cosθ)?",
    options: [
      { text: "The cart's mass m — it cancels out of the force balance.", correct: true, whyWrong: null },
      { text: "The radius r.", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "r is right there under the root — sharper curve, lower limit. The absent quantity is MASS." },
      { text: "Gravity g.", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "g is in the formula — it sets the available grip. The absent quantity is MASS." },
      { text: "The slope angle θ.", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "cosθ is in the formula. The absent quantity is MASS — m cancels." },
    ],
  },
  {
    question: "You need the largest radius a cart at speed v can negotiate. Which rearrangement is right?",
    options: [
      { text: "r = v² / (g·cosθ) — from v² = g·r·cosθ.", correct: true, whyWrong: null },
      { text: "r = v / (g·cosθ)", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "Check units: m/s ÷ m/s² = s — that's TIME, not a radius. v must be squared." },
      { text: "r = g·cosθ / v²", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "Inverted — that gives 1/metres. From v² = g·r·cosθ: r = v²/(g·cosθ)." },
      { text: "r = v² · g·cosθ", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "Multiplying gives m³/s⁴ — dimensional nonsense. Divide: r = v²/(g·cosθ)." },
    ],
  },
];

const EXPLAIN_CURVE_VARIANTS = [
  {
    question: "Replay it in your head: along which path did the cart leave the track?",
    options: [
      { text: "Straight along the tangent — once grip failed, Newton's 1st law took over.", correct: true, whyWrong: null },
      { text: "Flung radially outward by centrifugal force.", correct: false, tag: "CENTRIFUGAL_FORCE", whyWrong: "Watch the replay: it moves along the tangent, not outward along the radius. No outward force exists." },
      { text: "It was pushed backward off the track.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Its forward speed never dropped — it kept going straight while the track curved away." },
    ],
  },
  {
    question: "Which change would have prevented this crash?",
    options: [
      { text: "Entering slower (or a curve with larger radius) — reduce v²/r below the track's supply.", correct: true, whyWrong: null },
      { text: "A heavier cart, pressed harder onto the rails.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "Both demand and supply scale with mass — a heavier cart crashes at exactly the same speed." },
      { text: "More engine power through the curve.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Engine force is forward; the missing force was INWARD. More power would make it worse." },
    ],
  },
  {
    question: "At the instant the cart left the rails, what was true about the forces?",
    options: [
      { text: "The needed centripetal force m·v²/r exceeded the maximum the track could supply.", correct: true, whyWrong: null },
      { text: "Centrifugal force finally overpowered the track's grip.", correct: false, tag: "CENTRIFUGAL_FORCE", whyWrong: "No outward force overpowered anything — the INWARD supply just fell short of m·v²/r." },
      { text: "All forces balanced, so it flew off at constant velocity.", correct: false, tag: "NORMAL_EQUALS_WEIGHT", whyWrong: "If forces had balanced, it would have gone straight ONLY if it was already straight — gravity still acted; it left as a projectile." },
    ],
  },
  {
    question: "Why does braking BEFORE the curve fix this, even though the brakes act backward, not inward?",
    options: [
      { text: "Lower v slashes the needed inward force — it goes as v², so 20% slower needs 36% less force.", correct: true, whyWrong: null },
      { text: "Brakes press the cart down harder onto the track.", correct: false, tag: "NORMAL_EQUALS_WEIGHT", whyWrong: "Brakes don't add downforce. They cut v — and v² is what the inward demand depends on." },
      { text: "Slower carts have less centrifugal force to fight.", correct: false, tag: "CENTRIFUGAL_FORCE", whyWrong: "Right effect, wrong story: there's no outward force. Slower = less INWARD force needed (mv²/r)." },
    ],
  },
  {
    question: "The crash happened at the curve's TIGHTEST point. Why there?",
    options: [
      { text: "Smallest r → largest v²/r — the demand peaked exactly where the radius was minimum.", correct: true, whyWrong: null },
      { text: "The track is weakest where it bends most.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "The track didn't break — the physics did: v²/r is largest where r is smallest." },
      { text: "Bad luck — it could have happened anywhere.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "Fully predictable: compare v² with g·r·cosθ point by point; the minimum-r point fails first, every time." },
    ],
  },
];

// ─── PHASE 2 BANKS: Brake Zone (SUVAT) & Energy Gate ─────────────

const REASONING_BRAKE = [
  {
    question: "A STOP LINE is ahead. WHY is there a minimum distance in which you can stop?",
    options: [
      { text: "The brakes give a fixed maximum deceleration — from v, you need at least d = v²/(2a) of road.", correct: true, whyWrong: null },
      { text: "Reaction time is the only limit; with focus you can stop instantly.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Even with zero reaction time, shedding speed takes DISTANCE: v² = 2·a·d means d = v²/(2a) — never zero." },
      { text: "Heavier carts need longer; lighter ones stop on the spot.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "Friction braking gives a ≈ μ·g for ANY mass — m cancels, just like in curves." },
      { text: "Stopping distance grows in proportion to speed: double speed, double distance.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "d = v²/(2a): double speed = FOUR times the distance. This is the most dangerous misconception on real roads." },
    ],
  },
  {
    question: "You're approaching the stop line at speed v. When must braking BEGIN?",
    options: [
      { text: "At least d = v²/(2a) before the line — computed, not guessed.", correct: true, whyWrong: null },
      { text: "When the line 'looks close' — judgement beats formulas.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "Eyes misjudge v² badly. At 24 m/s you need 24 m; at 30 m/s, 37.5 m — more than 1.5× for 25% more speed." },
      { text: "Anywhere — brakes are strong enough to stop instantly.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Deceleration is finite (a = 12 m/s² here). v²=2ad fixes a minimum distance." },
      { text: "Exactly at the line — brakes act instantly there.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "At the line it's too late: you'd cross it still doing v. Begin v²/(2a) early." },
    ],
  },
  {
    question: "Two carts approach the line: one at 10 m/s, one at 20 m/s. Compared with the slow cart, the fast one needs…",
    options: [
      { text: "4× the stopping distance — d ∝ v².", correct: true, whyWrong: null },
      { text: "2× the stopping distance.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "v is SQUARED in d = v²/(2a): 2× speed → 4× distance." },
      { text: "The same — brakes don't care about speed.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "The KE to remove is ½mv² — four times more at double speed, and distance is where it goes." },
      { text: "√2× the stopping distance.", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "That would be d ∝ √v. Kinematics says d ∝ v²: 4×." },
    ],
  },
];

const FORMULA_BRAKE = [
  {
    question: "Which equation gives the minimum braking distance — and why?",
    options: [
      { text: "d = v²/(2a) — from v² = u² − 2·a·d with final speed 0.", correct: true, whyWrong: null },
      { text: "d = v·t — distance is speed times time.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Speed isn't constant while braking, and t isn't known. Use the SUVAT equation without t: v² = u² − 2ad." },
      { text: "d = ½·a·t² — distance under acceleration.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Valid from REST with known t. Here you know speeds, not time — pick v² = u² − 2ad." },
      { text: "d = v²·2a — speed squared times twice the deceleration.", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "Units: (m²/s²)(m/s²) ≠ metres. DIVIDE by 2a: d = v²/(2a)." },
    ],
  },
  {
    question: "You know initial speed u, final speed 0, deceleration a — but NOT the time. Which SUVAT equation is the right tool?",
    options: [
      { text: "v² = u² − 2·a·s — the only one with no t in it.", correct: true, whyWrong: null },
      { text: "v = u − a·t", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Contains t, which you don't know. Choose the equation whose variables match your data: v² = u² − 2as." },
      { text: "s = u·t − ½·a·t²", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Two unknowns (s and t) — unsolvable alone. v² = u² − 2as has only s unknown." },
      { text: "s = (u+v)·t/2", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Again t. SUVAT strategy: list knowns, pick the equation missing the unknown you DON'T need." },
    ],
  },
];

const EXPLAIN_BRAKE = [
  {
    question: "You crossed the stop line and hit the barrier. WHY?",
    options: [
      { text: "Braking began less than v²/(2a) before the line — the remaining distance couldn't absorb the speed.", correct: true, whyWrong: null },
      { text: "The brakes randomly failed.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "They delivered exactly a = 12 m/s², as always. The DISTANCE was too short for the speed: physics, not luck." },
      { text: "The cart was too heavy to stop.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "Mass cancels in friction braking (a ≈ μg). The error was distance: d = v²/(2a)." },
    ],
  },
  {
    question: "Watching the crash: the speedometer fell steadily but you still hit the barrier. What does that tell you?",
    options: [
      { text: "Deceleration was working — there simply wasn't enough distance left for v to reach zero.", correct: true, whyWrong: null },
      { text: "Falling speed means you would have stopped — the wall came too early.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "The wall didn't move! v² = u² − 2ad says how much road the stop NEEDS — you started with less." },
      { text: "Brakes weaken at high speed.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "a was constant. High speed needs quadratically more distance, not because brakes weaken." },
    ],
  },
];

const REASONING_CREST = [
  {
    question: "ENGINE-OFF CLIMB ahead (height Δh). WHY is there a minimum entry speed?",
    options: [
      { text: "With the engine off, only kinetic energy can pay for the climb: ½mv² must cover mgΔh (plus friction).", correct: true, whyWrong: null },
      { text: "Slow carts grip the slope better and crawl up regardless.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "Grip doesn't lift you. Climbing costs ENERGY (mgΔh); without engine, KE is the only account to pay from." },
      { text: "Heavier carts need more speed for the same hill.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "½mv² ≥ mgΔh — mass cancels: v_min = √(2gΔh) is the same for every cart." },
      { text: "There's no minimum — momentum carries you over eventually.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Gravity drains speed as you climb: v² = u² − 2gΔh. If u² < 2gΔh, v hits zero before the crest — and you roll back." },
    ],
  },
  {
    question: "Halfway up the engine-off climb, your speed has dropped. Where did that kinetic energy GO?",
    options: [
      { text: "Converted to gravitational potential energy (and a little to friction heat).", correct: true, whyWrong: null },
      { text: "It vanished — speed is just lost on hills.", correct: false, tag: "FORMULA_ENERGY", whyWrong: "Energy never vanishes; it transforms. Your missing ½mv² is sitting in mgΔh — and you get it back going down!" },
      { text: "The brakes absorbed it automatically.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "No brakes acted. Gravity converted KE → PE — that's what a hill IS, energetically." },
      { text: "Air resistance ate all of it.", correct: false, tag: "FORMULA_ENERGY", whyWrong: "Drag takes a small cut; the dominant transfer at these speeds is KE → PE = mgΔh." },
    ],
  },
];

const FORMULA_CREST = [
  {
    question: "Which equation gives the minimum entry speed for the engine-off climb of height Δh?",
    options: [
      { text: "v_min = √(2·g·Δh) — energy conservation: ½mv² = mgΔh.", correct: true, whyWrong: null },
      { text: "v_min = √(g·r·cosθ) — the curve formula.", correct: false, tag: "FORMULA_ENERGY", whyWrong: "That's the CIRCULAR limit (needs a radius). A climb is an ENERGY problem: ½mv² vs mgΔh." },
      { text: "v_min = g·Δh", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "Units: (m/s²)(m) = m²/s² — that's v². Take the root: √(2gΔh)." },
      { text: "v_min = 2·g·Δh / m", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "Mass cancels before you ever divide by it: ½mv² = mgΔh → v² = 2gΔh. And check the units!" },
    ],
  },
];

const EXPLAIN_CREST = [
  {
    question: "The cart slowed, stopped short of the crest, and rolled back. WHY?",
    options: [
      { text: "Entry KE was less than mgΔh — the energy account ran dry before the top.", correct: true, whyWrong: null },
      { text: "The slope was too steep for the wheels to hold.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "Wheels held fine — the cart rolled, it didn't slide. The shortfall was ENERGY: ½mv² < mgΔh." },
      { text: "A heavier load would have made it over.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "Both KE and the climb cost scale with m — mass cancels. Only more SPEED (or a lower hill) helps." },
    ],
  },
];

// ─── PHASE 2 NEW: Three-Corridor SUVAT banks ─────────────────────
// Corridor A — predict FINAL SPEED after acceleration from rest
// kind = "accel"

const REASONING_ACCEL = [
  {
    question: "The maglev sled starts from rest and accelerates steadily down Corridor A. WHY does it keep speeding up even though it starts from zero?",
    options: [
      { text: "A constant net force acts along the corridor — by Newton's 2nd law F=ma, constant a means speed grows linearly: v = u + at.", correct: true, whyWrong: null },
      { text: "It builds up momentum that 'carries it forward' — the momentum gives it an extra push.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Momentum is the RESULT of acceleration, not its cause. What accelerates the sled is the net force. Momentum doesn't push — force does." },
      { text: "Heavier sleds accelerate faster because they press harder onto the guide rail.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "F = ma → a = F/m. For the same force, a HEAVIER sled has LESS acceleration. Mass is in the denominator." },
      { text: "The sled accelerates because it is moving — once it stops, it won't restart.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "That's the old 'impetus' idea. Motion doesn't cause acceleration — FORCE does. A sled at rest can accelerate from standstill if a force acts on it." },
    ],
  },
  {
    question: "The sled travels 200 m with constant acceleration a from rest. Which statement about its speed along the way is correct?",
    options: [
      { text: "Speed increases steadily — equal speed gains in equal time intervals (constant a).", correct: true, whyWrong: null },
      { text: "Speed increases fastest at the start, then levels off.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "That describes a non-constant (reducing) acceleration. The problem states constant a — speed grows uniformly." },
      { text: "Speed is the same throughout — it just moves faster at the end due to momentum.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Speed changes exactly because a ≠ 0. Constant acceleration means constantly changing speed." },
      { text: "The sled reaches max speed halfway, then slows under its own weight.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "No braking force is described. On a level corridor with constant thrust, speed grows all the way to the end." },
    ],
  },
];

const FORMULA_ACCEL = [
  {
    question: "You know: u = 0 (starts from rest), acceleration a, distance s. You want final speed v. Which SUVAT equation to use?",
    options: [
      { text: "v² = u² + 2·a·s — connects v, u, a, s without needing time.", correct: true, whyWrong: null },
      { text: "v = u + a·t — final speed from time.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Valid equation, but TIME t is unknown here. Pick the equation whose unknowns match your data: v² = u² + 2as has no t." },
      { text: "s = u·t + ½·a·t² — distance from time.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "That gives s if you know t. You know s, not t — use v² = u² + 2as to find v directly." },
      { text: "v = √(2·g·h) — speed from falling height h.", correct: false, tag: "FORMULA_ENERGY", whyWrong: "That's free-fall energy with gravity. This corridor has a magnetic launch force, not just gravity — and you know a, not h. Use v² = u² + 2as." },
    ],
  },
  {
    question: "From v² = u² + 2as with u = 0, the formula simplifies to v = √(2·a·s). What does doubling the corridor LENGTH do to the final speed?",
    options: [
      { text: "Multiplies v by √2 ≈ 1.41 — speed scales with the square root of distance.", correct: true, whyWrong: null },
      { text: "Doubles v — double the distance, double the speed.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "v = √(2as): v ∝ √s. Double s → v × √2, not ×2. Speed grows slower than distance." },
      { text: "Quadruples v — acceleration compounds.", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "v² = 2as: double s → v² doubles → v multiplied by √2. Not ×4." },
      { text: "Makes no difference — speed depends only on time, not distance.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "v² = u² + 2as explicitly contains s. Longer corridor = more distance to accelerate over = higher final speed." },
    ],
  },
];

const EXPLAIN_ACCEL = [
  {
    question: "Your predicted final speed was wrong. Looking at v² = u² + 2·a·s: what is the most likely error?",
    options: [
      { text: "Forgot to take the square root — v² = 2as gives v², not v.", correct: true, whyWrong: null },
      { text: "Used the wrong formula — should have used v = u + at.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "v = u + at is also valid but requires time. v² = u² + 2as is correct for this data set. The error is arithmetic, not formula choice." },
      { text: "Mass of the sled affects the final speed.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "v = √(2as) has no mass. The final speed depends only on a and s — mass cancels out when F = ma is substituted." },
    ],
  },
];

// Corridor B — predict STOPPING DISTANCE
// kind = "stopdist"

const REASONING_STOPDIST = [
  {
    question: "The sled is doing u m/s. Brakes give constant deceleration a. WHY does stopping distance grow with the SQUARE of speed, not linearly?",
    options: [
      { text: "KE = ½mv² must be removed by the brakes — double speed means 4× the energy to absorb, hence 4× the distance.", correct: true, whyWrong: null },
      { text: "Stopping distance is proportional to speed — double speed, double distance.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "This is the most dangerous misconception in road safety. d = v²/(2a): the v is SQUARED. 2× speed → 4× distance, not 2×." },
      { text: "Heavier sleds stop in shorter distances because their weight presses on the brakes.", correct: false, tag: "MASS_DEPENDENCE", whyWrong: "Friction braking: a = μg for any mass. Stopping distance d = v²/(2μg) — mass cancels completely." },
      { text: "Stopping distance depends on time, not speed — faster sleds stop in the same time.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Stopping time t = v/a does grow linearly — but distance d = v²/(2a) grows as v². The two behave differently." },
    ],
  },
];

const FORMULA_STOPDIST = [
  {
    question: "You know initial speed u, final speed 0, deceleration a — but NOT time. Which SUVAT equation gives stopping distance directly?",
    options: [
      { text: "v² = u² − 2·a·s → s = u²/(2a) — the only SUVAT without t.", correct: true, whyWrong: null },
      { text: "s = u·t + ½·a·t² — requires time t.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "t is unknown. Use the equation without t: v² = u² − 2as. Set v = 0: s = u²/(2a)." },
      { text: "s = (u + v)/2 · t — average speed × time.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Again requires t. v² = u² − 2as is self-contained: you know u, v=0, a — solve for s directly." },
      { text: "s = u·t — constant speed formula.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "That's for zero acceleration. The sled IS decelerating, so use v² = u² − 2as." },
    ],
  },
];

const EXPLAIN_STOPDIST = [
  {
    question: "The sled overshot and hit the barrier. Your distance estimate was too SHORT. Why does d = v²/(2a) give a larger distance than intuition suggests?",
    options: [
      { text: "Because KE = ½mv² grows with v SQUARED — a small speed increase needs a lot more stopping room.", correct: true, whyWrong: null },
      { text: "Because the brakes weakened at high speed.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Deceleration a was constant throughout. The extra distance is because v² grows faster than v — physics, not brake failure." },
      { text: "The barrier was placed incorrectly.", correct: false, tag: "GRAPH_AS_PICTURE", whyWrong: "The barrier is at the correct position. Your prediction of d was too small because you underestimated the v² effect." },
    ],
  },
];

// Corridor C — predict LAUNCH SPEED to arrive at target speed
// kind = "launchspeed"

const REASONING_LAUNCHSPEED = [
  {
    question: "Corridor C decelerates the sled (friction/air drag). You need to ARRIVE at the loop entry with speed v_target. WHY must the launch speed be HIGHER than v_target?",
    options: [
      { text: "Energy is lost to friction along the corridor — the sled must start with enough KE to still have v_target after losing some to drag.", correct: true, whyWrong: null },
      { text: "A higher launch speed compensates for gravity pulling the sled sideways.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "The corridor is level — gravity has no component along the track. The loss is from friction/drag, which requires extra initial speed." },
      { text: "The sled needs momentum to 'push through' the deceleration.", correct: false, tag: "FORCE_IN_MOTION_DIRECTION", whyWrong: "Momentum isn't a stored resource that 'pushes through'. The sled decelerates by a = F/m every second — you need enough starting speed for v to still be ≥ v_target at the end." },
      { text: "Launch speed doesn't matter — brakes can fine-tune the entry speed.", correct: false, tag: "SPEED_EQUALS_STABILITY", whyWrong: "Brakes can only slow you DOWN, not speed you up. If launch speed is too low, you'll arrive below v_target — no recovery possible." },
    ],
  },
];

const FORMULA_LAUNCHSPEED = [
  {
    question: "You know: target arrival speed v, deceleration a over distance s. Which equation gives the required launch speed u?",
    options: [
      { text: "v² = u² − 2·a·s → u = √(v² + 2·a·s) — rearrange for u.", correct: true, whyWrong: null },
      { text: "u = v + a·t — but we don't know t.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "Time is unknown. Use v² = u² − 2as: both s and v are known, solve for u = √(v² + 2as)." },
      { text: "u = v − a·s — subtract deceleration × distance.", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "Check units: a·s = (m/s²)(m) = m²/s² — that's a speed SQUARED, not a speed. You can't subtract it from v directly." },
      { text: "u = v² + 2·a·s — no square root needed.", correct: false, tag: "UNITS_DIMENSIONS", whyWrong: "v² + 2as has units m²/s² — that's u², not u. Take the square root: u = √(v² + 2as)." },
    ],
  },
];

const EXPLAIN_LAUNCHSPEED = [
  {
    question: "The sled arrived at the loop entry too slowly and stalled. What went wrong with the calculation?",
    options: [
      { text: "Launch speed u was too low — u must satisfy u² = v² + 2·a·s, not u = v + 2as.", correct: true, whyWrong: null },
      { text: "The loop needs less speed than calculated — the formula overestimates.", correct: false, tag: "FORMULA_ENERGY", whyWrong: "The loop's entry requirement v_target is fixed by circular motion physics. Your launch calculation underdelivered — not the formula's fault." },
      { text: "Air resistance is negligible so the formula shouldn't apply.", correct: false, tag: "FORMULA_KINEMATICS", whyWrong: "The deceleration a models the combined drag. Even small a over 300 m matters — and u² = v² + 2as shows the gap clearly." },
    ],
  },
];

// ─── PHASE 2 NEW accessors ───
export function getAccelReasoning() { return withIds(pickVariant(REASONING_ACCEL, "R_accel")); }
export function getAccelFormula() { return withIds(pickVariant(FORMULA_ACCEL, "F_accel")); }
export function getAccelExplain() { return withIds(pickVariant(EXPLAIN_ACCEL, "E_accel")); }
export function getStopDistReasoning() { return withIds(pickVariant(REASONING_STOPDIST, "R_stopdist")); }
export function getStopDistFormula() { return withIds(pickVariant(FORMULA_STOPDIST, "F_stopdist")); }
export function getStopDistExplain() { return withIds(pickVariant(EXPLAIN_STOPDIST, "E_stopdist")); }
export function getLaunchSpeedReasoning() { return withIds(pickVariant(REASONING_LAUNCHSPEED, "R_launch")); }
export function getLaunchSpeedFormula() { return withIds(pickVariant(FORMULA_LAUNCHSPEED, "F_launch")); }
export function getLaunchSpeedExplain() { return withIds(pickVariant(EXPLAIN_LAUNCHSPEED, "E_launch")); }

// ─── PHASE 2 accessors ───
export function getBrakeReasoning() { return withIds(pickVariant(REASONING_BRAKE, "R_brake")); }
export function getBrakeFormula() { return withIds(pickVariant(FORMULA_BRAKE, "F_brake")); }
export function getBrakeExplain() { return withIds(pickVariant(EXPLAIN_BRAKE, "E_brake")); }
export function getCrestReasoning() { return withIds(pickVariant(REASONING_CREST, "R_crest")); }
export function getCrestFormula() { return withIds(pickVariant(FORMULA_CREST, "F_crest")); }
export function getCrestExplain() { return withIds(pickVariant(EXPLAIN_CREST, "E_crest")); }

// Draw-without-repeat: cycle through a shuffled order, reshuffle when spent
const _order = {};
function pickVariant(pool, key) {
  if (!_order[key] || _order[key].length === 0) {
    _order[key] = shuffle(pool.map((_, i) => i));
  }
  return pool[_order[key].pop()];
}

// ─── Accessors (return shuffled copies with stable ids) ──────────
function withIds(q) {
  return {
    question: q.question,
    options: shuffle(q.options.map((o, i) => ({ ...o, id: i }))),
  };
}

export function getReasoningQuestion(kind) {
  if (kind === "curve") return withIds(pickVariant([REASONING.curve, ...REASONING_CURVE_VARIANTS], "R_curve"));
  if (kind === "brake") return getBrakeReasoning();
  if (kind === "energycrest") return getCrestReasoning();
  if (kind === "accel") return getAccelReasoning();
  if (kind === "stopdist") return getStopDistReasoning();
  if (kind === "launchspeed") return getLaunchSpeedReasoning();
  const q = REASONING[kind] || REASONING.curve;
  return withIds(q);
}

export function getFormulaQuestion(kind) {
  if (kind === "curve") return withIds(pickVariant([FORMULA.curve, ...FORMULA_CURVE_VARIANTS], "F_curve"));
  if (kind === "brake") return getBrakeFormula();
  if (kind === "energycrest") return getCrestFormula();
  if (kind === "accel") return getAccelFormula();
  if (kind === "stopdist") return getStopDistFormula();
  if (kind === "launchspeed") return getLaunchSpeedFormula();
  const q = FORMULA[kind] || FORMULA.curve;
  return withIds(q);
}

export function getExplainQuestion(kind) {
  if (kind === "curve") return withIds(pickVariant([EXPLAIN.curve, ...EXPLAIN_CURVE_VARIANTS], "E_curve"));
  if (kind === "brake") return getBrakeExplain();
  if (kind === "energycrest") return getCrestExplain();
  if (kind === "accel") return getAccelExplain();
  if (kind === "stopdist") return getStopDistExplain();
  if (kind === "launchspeed") return getLaunchSpeedExplain();
  const q = EXPLAIN[kind] || EXPLAIN.curve;
  return withIds(q);
}
